/**
 * Tiny hash router for SPA navigation.
 * Pseudocode:
 * - Parse location.hash into { kind: 'home' } or { kind: 'app', id }
 * - Expose a readable store `route`
 * - Listen to hashchange and update store
 */
import { readable } from 'svelte/store';
import { trackPageView, trackAppView } from './analytics';

export type Route =
  | { kind: 'home' }
  | { kind: 'app'; id: string }
  | { kind: 'storage-apps' }
  | { kind: 'privacy' }
  | { kind: 'terms' }
  | { kind: 'accessibility' }
  | { kind: 'about' }
  | { kind: 'feedback' }
  | { kind: 'settings' }
  | { kind: 'privacy-proof' }
  | { kind: 'unknown' };

function parseHash(): Route {
  // Extract hash path without leading '#'
  const raw = (location.hash || '').replace(/^#\/?/, ''); // remove # and optional /
  if (!raw) return { kind: 'home' };
  const [first, second] = raw.split('/');
  if (first === 'storage-apps') return { kind: 'storage-apps' };
  if (first === 'privacy') return { kind: 'privacy' };
  if (first === 'terms') return { kind: 'terms' };
  if (first === 'accessibility') return { kind: 'accessibility' };
  if (first === 'about') return { kind: 'about' };
  if (first === 'feedback') return { kind: 'feedback' };
  if (first === 'settings') return { kind: 'settings' };
  if (first === 'privacy-proof') return { kind: 'privacy-proof' };
  if (first === 'apps' && second) return { kind: 'app', id: second };
  return { kind: 'unknown' };
}

export const route = readable<Route>(parseHash(), (set) => {
  // Update on hash changes
  const onChange = () => {
    const newRoute = parseHash();
    set(newRoute);

    // Track page view for analytics
    if (newRoute.kind === 'home') {
      trackPageView('/');
    } else if (newRoute.kind === 'app') {
      trackAppView(newRoute.id, `/apps/${newRoute.id}`);
    } else if (newRoute.kind === 'storage-apps') {
      trackPageView('/storage-apps');
    } else if (newRoute.kind === 'privacy') {
      trackPageView('/privacy');
    } else if (newRoute.kind === 'terms') {
      trackPageView('/terms');
    } else if (newRoute.kind === 'accessibility') {
      trackPageView('/accessibility');
    } else if (newRoute.kind === 'about') {
      trackPageView('/about');
    } else if (newRoute.kind === 'feedback') {
      trackPageView('/feedback');
    } else if (newRoute.kind === 'settings') {
      trackPageView('/settings');
    } else if (newRoute.kind === 'privacy-proof') {
      trackPageView('/privacy-proof');
    }
  };
  window.addEventListener('hashchange', onChange);

  // Track initial page load
  const initialRoute = parseHash();
  if (initialRoute.kind === 'home') {
    trackPageView('/');
  } else if (initialRoute.kind === 'app') {
    trackAppView(initialRoute.id, `/apps/${initialRoute.id}`);
  } else if (initialRoute.kind === 'storage-apps') {
    trackPageView('/storage-apps');
  } else if (initialRoute.kind === 'privacy') {
    trackPageView('/privacy');
  } else if (initialRoute.kind === 'terms') {
    trackPageView('/terms');
  } else if (initialRoute.kind === 'accessibility') {
    trackPageView('/accessibility');
  } else if (initialRoute.kind === 'about') {
    trackPageView('/about');
  } else if (initialRoute.kind === 'feedback') {
    trackPageView('/feedback');
  } else if (initialRoute.kind === 'settings') {
    trackPageView('/settings');
  } else if (initialRoute.kind === 'privacy-proof') {
    trackPageView('/privacy-proof');
  }

  return () => window.removeEventListener('hashchange', onChange);
});

export function setHashRouter(): void {
  // Initialize hash if empty
  if (!location.hash) {
    location.hash = '#/'; // home route
  }
}
